import { useState } from 'react';
import type { ReportStatus } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';
import { setReportApproval } from '../../services/report.service.ts';

/**
 * The coach's controls, at the top of the report rather than the foot of it.
 *
 * Signing a report off was the last thing on a long page, below the synthesis
 * and easy to mistake for the button that approves the synthesis — which is
 * how a coach finished their review and left the report a draft nobody else
 * could open. It belongs where the reader arrives, next to the status it
 * changes.
 *
 * The confirmation names who gains access, because that is the whole content
 * of the decision: approving is not a tidy-up, it is the moment the leader and
 * P&O can read what colleagues said about them.
 */
export const ReviewBar = ({
  participantId,
  status,
  firstName,
  onChanged,
}: {
  participantId: string;
  status: ReportStatus | null;
  firstName: string;
  onChanged: (status: ReportStatus) => void;
}) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const released = status === 'APPROVED' || status === 'PUBLISHED';

  const apply = async (approved: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const result = await setReportApproval(participantId, approved);
      onChanged(result.status as ReportStatus);
      setConfirming(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      // The coach's controls are not part of the document they are
      // approving: on paper the report must look like the leader's copy.
      className={`mt-4 rounded-2xl border px-5 py-4 shadow-card print:hidden ${
        released ? 'border-tint-line bg-tint' : 'border-line bg-paper'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
            {t('report.reviewKicker')}
          </p>
          <p className="mt-1 text-[14.5px] font-bold">
            {released ? t('report.reviewReleased', { name: firstName }) : t('report.reviewDraft')}
          </p>
          <p className="mt-0.5 text-[12.5px] text-soft">
            {released ? t('report.reviewReleasedHelp') : t('report.reviewDraftHelp')}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {released ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void apply(false)}
              className="cursor-pointer rounded-xl border border-line bg-paper px-3.5 py-2 text-[13px] font-semibold text-soft transition hover:border-[var(--color-critical)] hover:text-[var(--color-critical)] disabled:opacity-40"
            >
              {t('report.reviewRevert')}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => (confirming ? void apply(true) : setConfirming(true))}
              className={`cursor-pointer rounded-xl px-4 py-2.5 text-[14px] font-bold text-white shadow-action transition disabled:opacity-40 ${
                confirming ? 'bg-[var(--color-critical)]' : 'bg-teal hover:bg-teal-deep'
              }`}
            >
              {confirming ? t('report.reviewConfirm') : t('report.reviewApprove')}
            </button>
          )}
        </div>
      </div>

      {confirming && !released && (
        <p className="mt-2.5 border-t border-line-soft pt-2.5 text-[12.5px] text-soft">
          {t('report.reviewConfirmNote', { name: firstName })}
        </p>
      )}
      {error && <p className="mt-2 text-[12.5px] text-[var(--color-critical)]">{error}</p>}
    </section>
  );
};
